import { useGetClientRowQuery } from '@/features/clients/clientsApi';
import { useGetDealRowQuery } from '@/features/deals/dealsApi';
import { getDealClientId } from '@/features/deals/helper';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import getCorrectFolderOwner from '@/hooks/getCorrectFolderOwner';
import { useClientStorageHooks } from '@/hooks/useClientStorageHooks';
import { safelyParseDate } from '@/utils/date';
import { useFormikContext } from 'formik';
import React, { useEffect, useRef } from 'react';

const FormikNoteObject = () => {
	const formik = useFormikContext();
	const { deal, client } = formik.values;

	const clientRowId = client?.clientId ?? getDealClientId(deal);
	const clientRowQuery = useGetClientRowQuery(clientRowId, {
		skip: !clientRowId,
	});
	const clientRow = clientRowQuery.currentData;

	const dealRowQuery = useGetDealRowQuery(deal?.dealId, {
		skip: !deal?.dealId,
	});
	const dealRow = dealRowQuery.currentData;

	const { correctOwner: folderOwnerId, loading: loadingOwner } = getCorrectFolderOwner({
		deal: dealRow,
		client: clientRow,
	});

	useEffect(() => {
		if (
			clientRow &&
			(clientRow?.driveId != client?.driveId ||
				clientRow?.clientFolderDriveId != client?.clientFolderDriveId ||
				!client ||
				client?.isTest)
		) {
			formik.setFieldValue('client', clientRow);
		}
	}, [client, clientRow?.driveId, clientRow?.clientFolderDriveId, client?.isTest]);

	useEffect(() => {
		if (
			dealRow &&
			(dealRow?.driveId != deal?.driveId ||
				dealRow?.clientFolderDriveId != deal?.clientFolderDriveId ||
				!deal ||
				deal?.isTest)
		) {
			formik.setFieldValue('deal', dealRow);
		}
	}, [deal, dealRow?.driveId, dealRow?.clientFolderDriveId, deal?.isTest]);

	const { data: folderOwner } = useGetUserDetailsQuery(
		{ userId: folderOwnerId },
		{ skip: !folderOwnerId || deal?.isTest || client?.isTest },
	);

	const isValidSharepoint =
		folderOwner?.sharepointSite != null &&
		folderOwner?.clientSiteSuffix != null &&
		folderOwner?.clientFolder != null &&
		folderOwner?.clientSite != null;

	const { createClientFolder, updateClientGraphProperties, getClientDriveId } =
		useClientStorageHooks(dealRow?.brokerGroupId ?? clientRow?.brokerGroupId);

	const createSharepoint = async () => {
		try {
			const date = safelyParseDate(dealRow?.createDate) ?? new Date();

			await createClientFolder(dealRow?.dealId, {
				deal: dealRow,
				client: clientRow,
				objectType: dealRow?.objectType,
				...(dealRow?.yearRoot ? { yearRoot: dealRow?.yearRoot.split('/')[1] } : { date }),
			});
		} catch (error) {
			console.log(`Error creating deal folder for deal ${dealRow?.dealName}`, error);
		}
	};

	useEffect(() => {
		const updateClientMsGraph = async () => {
			if (clientRow?.driveId && clientRow?.clientFolderDriveId) {
				await getClientDriveId(clientRow?.clientId);
			} else {
				await updateClientGraphProperties({ clientRow });
			}
		};
		const updateDealMsGraph = async () => {
			const res = await updateClientGraphProperties({ clientRow, dealRow });
			if (
				!res?.driveId &&
				isValidSharepoint &&
				folderOwner?.sharepointSiteId &&
				folderOwner?.clientFolderDriveId
			) {
				await createSharepoint();
			}
		};

		if (!dealRow && clientRow && clientRow?.canViewClient && !client?.isTest) {
			updateClientMsGraph();
		} else if (
			dealRow?.canViewDeal &&
			clientRow &&
			dealRow &&
			!deal?.isTest &&
			(!dealRow?.driveId || !dealRow?.clientFolderDriveId)
		) {
			updateDealMsGraph();
		}
	}, [folderOwner, clientRow, dealRow, isValidSharepoint, client?.isTest, deal?.isTest]);

	return <></>;
};

export default FormikNoteObject;
